import React from "react";
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm, FormProvider, useFormContext } from "react-hook-form";

/* ── Individual payment row ── */
export const paymentTypesSchema = z.object({
  payment_type: z
    .union([z.string(), z.number()])
    .transform((val) => Number(val))
    .refine((val) => !isNaN(val) && val >= 1, {
      message: "Please select a payment mode",
    }),
  amount_paid: z
    .union([z.string(), z.number()])
    .transform((val) => parseFloat(val))
    .refine((val) => !isNaN(val) && val > 0, {
      message: "Amount must be greater than zero",
    }),
});

/* ── Full receipt form ── */
export const receiptDataSchema = z
  .object({
    payment_modes: z
      .array(paymentTypesSchema)
      .min(1, { message: "At least one payment mode is required" }),
  })
  .superRefine((data, ctx) => {
    /* Check for duplicate payment types */
    const types = data.payment_modes.map((pm) => pm.payment_type);
    const seen  = new Set();

    types.forEach((type, index) => {
      if (seen.has(type)) {
        ctx.addIssue({
          code:    z.ZodIssueCode.custom,
          message: "This payment mode has already been added — remove the duplicate",
          path:    ["payment_modes", index, "payment_type"],
        });
      } else {
        seen.add(type);
      }
    });
  });

/* ── Hooks ── */
export const useFormCreateReceipt = () =>
  useForm({
    resolver:      zodResolver(receiptDataSchema),
    mode:          "onChange",       // validate on every change so errors show live
    defaultValues: {
      payment_modes: [{ payment_type: "", amount_paid: 0 }],
    },
  });

/* ── Providers ── */
export const FormProviderCreateReceipt = ({ children }) => {
  const methods = useFormCreateReceipt();
  return <FormProvider {...methods}>{children}</FormProvider>;
};

export const useFormContextCreateReceipt = () => useFormContext();